// Chakra imports
import {
    Box,
    Button,
    FormControl,
    FormLabel,
    Progress,
    Text,
    Select,
    useColorModeValue,
    Badge,
    VStack,
    Switch,
    Alert,
    AlertIcon,
    HStack,
    Icon
  } from "@chakra-ui/react";
  import React, { useState, useEffect } from "react";
  import { FaShoppingCart, FaMoneyBillWave } from "react-icons/fa";
  import { useAppContext } from "../../../../context/appContext";
  import { getCustomer, updateCustomer, getProducts, createOrderCuota } from "../../../../services/woo";

  function BatchUsersSubscription({title, customers, onSubscriptionChanged}) {
    const titleColor = useColorModeValue("teal.300", "teal.200");
    const textColor = useColorModeValue("gray.700", "white");
    const bgColor = useColorModeValue("white", "gray.700");
    const { handleUpdateData } = useAppContext();
    
    const [selectedSubscription, setSelectedSubscription] = useState("");
    const [subscriptions, setSubscriptions] = useState([]);
    const [log, setLog] = useState([]);
    const [loading, setLoading] = useState(false);
    const [usersUpdated, setUsersUpdated] = useState([]);
    const [progress, setProgress] = useState(0);
    const [generateOrder, setGenerateOrder] = useState(false);
    const [selectedProduct, setSelectedProduct] = useState(null);

    // Cargar productos/suscripciones disponibles
    useEffect(() => {
      const loadSubscriptions = async () => {
        try {
          const products = await getProducts();
          console.log('Productos disponibles:', products);
          setSubscriptions(products || []);
        } catch (error) {
          console.error('Error cargando productos:', error);
        }
      };
      
      loadSubscriptions();
    }, []);

    // Actualizar producto seleccionado cuando cambia la suscripción
    useEffect(() => {
      if (selectedSubscription && subscriptions.length > 0) {
        const product = subscriptions.find(p => p.name === selectedSubscription);
        setSelectedProduct(product);
      } else {
        setSelectedProduct(null);
      }
    }, [selectedSubscription, subscriptions]);

    const updateSubscriptions = async () => {
      if (!selectedSubscription) {
        alert('Debes seleccionar una suscripción');
        return;
      }

      setLoading(true);
      setUsersUpdated([]);
      setProgress(0);

      const totalUsers = customers.length;
      let processedUsers = 0;

      for (const customer of customers) {
        try {
          const customer_id = customer.id;
          
          // Obtener información actualizada del cliente
          const getCustomerInfo = await getCustomer(customer_id);
          const { meta_data } = getCustomerInfo.data;
          const currentSubscription = meta_data?.find(m => m.key === 'suscription_name')?.value;
          
          // Actualizar suscripción del cliente
          const meta_data_customer = {
            meta_data: [
              { key: "suscription_name", value: selectedSubscription }
            ]
          };
          
          const customerUpdated = await updateCustomer(customer_id, meta_data_customer);
          console.log('Cliente actualizado:', customerUpdated);
          
          // Crear orden si está habilitado
          if (generateOrder && selectedProduct) {
            const monthNames = ["Enero", "Febrero", "Marzo", "Abril", "Mayo", "Junio",
              "Julio", "Agosto", "Septiembre", "Octubre", "Noviembre", "Diciembre"];
            
            const mes_actual = new Date().getMonth();
            const anio_actual = new Date().getFullYear();
            const mes_actual_nombre = monthNames[mes_actual];
            const mes_actual_format = String(mes_actual + 1).padStart(2, '0');

            const order_meta_data = [
              {
                key: 'mes',
                value: anio_actual + '-' + mes_actual_format,
              },
              {
                key: 'concepto',
                value: selectedProduct.name,
              },
              {
                key: 'monto',
                value: selectedProduct.price,
              },
              {
                key: 'fecha_vencimiento',
                value: '15 ' + mes_actual_nombre,
              },
              {
                key: 'order_type',
                value: 'subscription_payment',
              }
            ];
            
            try {
              const orderCreated = await createOrderCuota(customer, selectedProduct, order_meta_data);
              console.log('Orden creada:', orderCreated);
            } catch (orderError) {
              console.error('Error creando orden:', orderError);
            }
          }
          
          setUsersUpdated(prev => [...prev, {
            id: customer_id,
            name: `${customer.first_name} ${customer.last_name}`,
            previousSubscription: currentSubscription,
            newSubscription: selectedSubscription,
            success: true
          }]);
          
        } catch (error) {
          console.error('Error procesando cliente:', error);
          setUsersUpdated(prev => [...prev, {
            id: customer.id,
            name: `${customer.first_name} ${customer.last_name}`,
            success: false,
            error: error.message
          }]);
        }
        
        processedUsers++;
        const progressPercentage = (processedUsers / totalUsers) * 100;
        setProgress(progressPercentage);
      }

      setLoading(false);
      handleUpdateData();
      
      // Callback para notificar cambios
      if (onSubscriptionChanged) {
        onSubscriptionChanged();
      }
      
      setLog([...log, {
        'subscriptions_updated': usersUpdated,
        'timestamp': new Date().toISOString()
      }]);
    };

    return (
      <VStack spacing={4} align="stretch">
        <Text fontSize={'lg'} fontWeight="semibold">
          <Badge fontSize='0.8em' colorScheme='blue' mr={2}>
            {customers.length} usuarios seleccionados
          </Badge>
          Cambiar Suscripción
        </Text>

        <FormControl>
          <FormLabel>Seleccionar Nueva Suscripción</FormLabel>
          <Select 
            value={selectedSubscription} 
            onChange={(e) => setSelectedSubscription(e.target.value)}
            placeholder="Seleccionar suscripción"
            size="lg"
          >
            {subscriptions.map((subscription) => (
              <option key={subscription.id} value={subscription.name}>
                {subscription.name} - ${subscription.price}
              </option>
            ))}
          </Select>
        </FormControl>

        {selectedProduct && (
          <Alert status="info" borderRadius="md">
            <AlertIcon />
            <Box>
              <Text fontSize="sm" fontWeight="medium">
                Suscripción seleccionada: {selectedProduct.name}
              </Text>
              <Text fontSize="sm">
                Precio: ${selectedProduct.price}
              </Text>
            </Box>
          </Alert>
        )}

        <FormControl>
          <HStack justify="space-between" align="center">
            <VStack align="flex-start" spacing={1}>
              <Text fontSize="sm" fontWeight="medium">
                ¿Generar orden de pago automática?
              </Text>
              <Text fontSize="xs" color="gray.500">
                Crea una orden para el mes actual con la nueva suscripción
              </Text>
            </VStack>
            <Switch 
              id="generate-order" 
              colorScheme="teal" 
              size="lg"
              isChecked={generateOrder} 
              onChange={(e) => setGenerateOrder(e.target.checked)} 
            />
          </HStack>
        </FormControl>

        {usersUpdated.length > 0 && (
          <Box>
            <Text fontSize="sm" color="gray.600" mb={2}>
              Progreso: {usersUpdated.length} de {customers.length} usuarios procesados
            </Text>
            <Progress 
              value={progress} 
              colorScheme="teal" 
              borderRadius="md"
              size="lg"
            />
            
            <VStack spacing={2} mt={3} align="stretch">
              {usersUpdated.slice(-3).map((user, index) => (
                <Box key={index} p={2} bg={user.success ? "green.50" : "red.50"} borderRadius="md">
                  <HStack justify="space-between">
                    <Text fontSize="sm" fontWeight="medium">
                      {user.name}
                    </Text>
                    <Badge colorScheme={user.success ? "green" : "red"}>
                      {user.success ? "Actualizado" : "Error"}
                    </Badge>
                  </HStack>
                  {user.success && (
                    <Text fontSize="xs" color="gray.600">
                      {user.previousSubscription ? `${user.previousSubscription} → ` : ''}
                      {user.newSubscription}
                    </Text>
                  )}
                  {user.error && (
                    <Text fontSize="xs" color="red.600">
                      Error: {user.error}
                    </Text>
                  )}
                </Box>
              ))}
            </VStack>
          </Box>
        )}

        <Button
          colorScheme="blue"
          size="lg"
          onClick={updateSubscriptions}
          isLoading={loading}
          isDisabled={!selectedSubscription}
          leftIcon={<Icon as={generateOrder ? FaMoneyBillWave : FaShoppingCart} />}
        >
          {loading ? 
            `Procesando... ${Math.round(progress)}%` : 
            generateOrder ? 
              'ACTUALIZAR SUSCRIPCIONES Y GENERAR ÓRDENES' : 
              'ACTUALIZAR SUSCRIPCIONES'
          }
        </Button>
      </VStack>
    );
  }
  
export default BatchUsersSubscription;